import React, { useCallback, useEffect, useRef } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

const PNG_PREFIX = "data:image/png;base64,";

/** `public/pencil-grey.svg` — hotspot matches lead tip (18,48); group rotates around that point. */
const SIGNATURE_PAD_CURSOR = (() => {
  const base = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
  // return `url("${base}/pencil-grey.svg") 18 48, crosshair`;
  // return `url("${base}/pencil-grey.svg") 2 22, crosshair`;
  return `url("${base}/pencil-grey.svg") 4 33, crosshair`;
})();

export type CanvasSignaturePadTestIds = {
  root?: string;
  canvas?: string;
  clear?: string;
  signingDate?: string;
};

const defaultTestIds: Required<CanvasSignaturePadTestIds> = {
  root: "canvas-signature-pad",
  canvas: "canvas-signature-canvas",
  clear: "canvas-signature-clear",
  signingDate: "canvas-signature-signing-date"
};

export type CanvasSignaturePadProps = {
  /** Called when the pad becomes non-empty or is cleared. Value is raw base64 (no `data:image/png;base64,` prefix). */
  onSignatureChange: (pngBase64: string | null) => void;
  /** Accessible label for the clear action (caller supplies i18n). */
  clearLabel: string;
  /** Drawing area height in CSS pixels. Width follows the container. */
  heightPx?: number;
  /** Formatted signing date string (optional; shown above the canvas when set). */
  formattedSigningDate?: string;
  /** Label before the date, e.g. i18n "Date" (optional; omit to show only the formatted string). */
  signingDateLabel?: string;
  /** Override `data-testid` values for tests or embedding contexts. */
  testIds?: CanvasSignaturePadTestIds;
};

export const CanvasSignaturePad: React.FC<CanvasSignaturePadProps> = ({
  onSignatureChange,
  clearLabel,
  heightPx = 160,
  formattedSigningDate,
  signingDateLabel,
  testIds
}) => {
  const ids = { ...defaultTestIds, ...testIds };
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number } | null>(null);
  const hasInkRef = useRef(false);
  const lastDimsRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const onSignatureChangeRef = useRef(onSignatureChange);
  const heightPxRef = useRef(heightPx);
  onSignatureChangeRef.current = onSignatureChange;
  heightPxRef.current = heightPx;

  const emitCurrent = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) {
      onSignatureChangeRef.current(null);
      return;
    }
    const dataUrl = canvas.toDataURL("image/png");
    const raw = dataUrl.startsWith(PNG_PREFIX) ? dataUrl.slice(PNG_PREFIX.length) : null;
    onSignatureChangeRef.current(raw);
  }, []);

  const resizeCanvas = useCallback((forceClear: boolean) => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) {
      return;
    }

    const dpr = window.devicePixelRatio ?? 1;
    const widthCss = Math.max(1, Math.floor(wrap.clientWidth));
    const heightCss = heightPxRef.current;
    const prev = lastDimsRef.current;
    if (!forceClear && prev.w === widthCss && prev.h === heightCss) {
      return;
    }
    lastDimsRef.current = { w: widthCss, h: heightCss };

    canvas.style.width = `${widthCss}px`;
    canvas.style.height = `${heightCss}px`;
    canvas.width = Math.floor(widthCss * dpr);
    canvas.height = Math.floor(heightCss * dpr);

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, widthCss, heightCss);

    hasInkRef.current = false;
    onSignatureChangeRef.current(null);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) {
      return;
    }

    resizeCanvas(true);
    const ro = new ResizeObserver(() => {
      resizeCanvas(false);
    });
    ro.observe(wrap);
    return () => {
      ro.disconnect();
    };
  }, [resizeCanvas, heightPx]);

  const canvasCoords = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      return null;
    }
    return { x, y };
  }, []);

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const pt = canvasCoords(event.clientX, event.clientY);
    if (!pt) {
      return;
    }
    drawingRef.current = true;
    lastRef.current = pt;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const last = lastRef.current;
    const pt = canvasCoords(event.clientX, event.clientY);
    if (!canvas || !ctx || !last || !pt) {
      return;
    }

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastRef.current = pt;
    hasInkRef.current = true;
  };

  const endStroke = () => {
    if (drawingRef.current) {
      drawingRef.current = false;
      lastRef.current = null;
      emitCurrent();
    }
  };

  const handleClear = () => {
    resizeCanvas(true);
  };

  return (
    <Stack spacing={1} data-testid={ids.root}>
      <Box
        ref={wrapRef}
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "#ffffff",
          touchAction: "none"
        }}
      >
        {formattedSigningDate && formattedSigningDate.length > 0 && (
          <Typography variant="body2" sx={{ px: 2, pt: 2, pb: 1 }} data-testid={ids.signingDate}>
            {signingDateLabel ? (
              <>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {signingDateLabel}
                </Box>
                {`: ${formattedSigningDate}`}
              </>
            ) : (
              formattedSigningDate
            )}
          </Typography>
        )}

        <canvas
          ref={canvasRef}
          data-testid={ids.canvas}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endStroke}
          onPointerCancel={endStroke}
          style={{ display: "block", cursor: SIGNATURE_PAD_CURSOR }}
        />
      </Box>
      <Box>
        <Button
          size="small"
          onClick={handleClear}
          data-testid={ids.clear}
          sx={{ backgroundColor: "#1976d2", color: "#ffffff" }}
        >
          {clearLabel}
        </Button>
      </Box>
    </Stack>
  );
};
